import {inject, injectable} from 'inversify'
import {action, computed, makeObservable, observable, reaction, runInAction, when} from 'mobx'
import moment from 'moment-timezone'
import i18next from 'i18next'
import 'reflect-metadata'

import {PayByLinkStatus, TABLE_STORAGE_KEYS, TableViewType} from '@/constants'
import {getPaymentLinksV2, PaymentLink, PaymentLinkRequest, PaymentLinkOptionMode} from '@/api'
import {openErrorNotification} from '@/utils'

import {IPayByLinkStore} from '@/pages/PayByLink/IPayByLinkStore'
import {TRangePickerContainerStore} from '@/components/containers/RangePickerContainer/TRangePickerContainerStore'
import {getColumns} from '~/code/pages/PayByLink/services'
import {
    PayByLinkCreateNewLinkStoreSymbol,
    PayByLinkFilterButtonStoreSymbol,
    PayByLinkNewPaymentLinkModalContainerStoreSymbol,
    PayByLinkOverviewModalContainerStoreSymbol,
    PayByLinkPaginationStoreSymbol,
    PayByLinkPaymentLinkDetailsStoreSymbol,
    PayByLinkRangePickerContainerStoreSymbol
} from '@/pages/PayByLink'
import {
    AvailableStoresDataStore,
    AvailableStoresDataStoreSymbol
} from '@/stores/store-and-terminals'
import {TModalContainerStore} from '@/components/containers/ModalContainer/TModalContainerStore'
import {payByLinkTabs} from '@/services/mocks'
import {
    INewPaymentLinkModalStore,
    TPaymentLinkDetailsModalStore
} from '@/pages/PayByLink/components'
import {IFilterContainerStore, PaginationContainerStoreType} from '@/components/containers'
import {statuses} from '@/pages/PayByLink/constants/statuses'
import {TOverviewModalStore} from '@/pages/PayByLink/components/OverviewModal/TOverviewModalStore'
import {LoadingState} from '@/types'
import {PeriodType} from '@/stores/date-picker'
import {storage} from '@/services/storage'
import {ProfileStoreSymbol} from '@/pages/Profile/Profile'
import {ProfileStoreInterface} from '@/pages/Profile'
import {PayByLinkFilterType} from '@/stores/pay-by-link/models/PayByLinkFilterType'

@injectable()
export class PayByLinkStore implements IPayByLinkStore {
    private readonly _rangePickerStore: TRangePickerContainerStore
    private readonly _overviewModalStore: TOverviewModalStore
    private readonly _newPaymentLinkModalStore: TModalContainerStore
    private readonly _paginationStore: PaginationContainerStoreType
    private readonly _createNewPaymentLinkStore: INewPaymentLinkModalStore
    private readonly _paymentLinkDetailsModalStore: TPaymentLinkDetailsModalStore
    private readonly _profileStore: ProfileStoreInterface
    private readonly _merchantStoresStore: AvailableStoresDataStore
    filterStore: IFilterContainerStore<PayByLinkFilterType>

    private _isInitialized = false

    mobileTableViewType = TableViewType.EXPANDED
    isLoading = false
    selectedPaymentLink: PaymentLink = {} as PaymentLink
    createdPaymentLink: PaymentLink = {} as PaymentLink
    _paymentLinks: PaymentLink[] = null
    _columns = getColumns(this)
    status: PayByLinkStatus = payByLinkTabs()[0].key
    statusSelectOptions = statuses()
    statusTabsOptions = payByLinkTabs()
    isNewPaymentLinkNotificationModalOpen = false

    get merchantId() {
        return this._profileStore?.merchant?.merchantId
    }

    get columns() {
        return this._columns
    }

    get request(): PaymentLinkRequest {
        const {initiatorEmail, customerName, description, type} = this.filterStore.filter
        const trimmedType = type?.trim()

        let modes: PaymentLinkOptionMode[] | null = null

        if (trimmedType) {
            modes =
                trimmedType === 'multiUse'
                    ? [
                          PaymentLinkOptionMode.REUSABLE_FIXED,
                          PaymentLinkOptionMode.REUSABLE_VARIABLE
                      ]
                    : [PaymentLinkOptionMode.ONE_OFF_FIXED]
        }

        return {
            ...this.preferences,
            initiatorEmail: initiatorEmail?.trim(),
            customerName: customerName?.trim(),
            description: description?.trim(),
            page: this._paginationStore.pageNumber,

            ...(modes && {linkOptions: {modes}})
        }
    }

    get preferences(): Omit<PaymentLinkRequest, 'page' | 'terminalId' | keyof PayByLinkFilterType> {
        const filterStatuses: PayByLinkStatus[] = statuses()
            .filter((item) => this.status === item.value)
            .map((item) => item.value)

        const backEndStatuses: string[] =
            this.status === PayByLinkStatus.active
                ? [...filterStatuses, PayByLinkStatus.viewed]
                : filterStatuses

        return {
            sort: 'created_date',
            order: 'desc',
            from: this._rangePickerStore?.startDate?.format(),
            to: this._rangePickerStore?.endDate?.format(),
            size: this._paginationStore.pageSize,
            statuses: this.status === PayByLinkStatus.all ? [] : backEndStatuses
        }
    }

    get paymentLinks(): PaymentLink[] {
        if (!this._paymentLinks && this.merchantId) {
            this.loadPaymentLinks()

            return []
        }

        return this._paymentLinks
    }

    constructor(
        @inject(PayByLinkRangePickerContainerStoreSymbol)
        rangePickerStore: TRangePickerContainerStore,
        @inject(PayByLinkOverviewModalContainerStoreSymbol)
        overviewModalStore: TOverviewModalStore,
        @inject(PayByLinkNewPaymentLinkModalContainerStoreSymbol)
        newPaymentLinkModalStore: TModalContainerStore,
        @inject(PayByLinkCreateNewLinkStoreSymbol)
        createNewPaymentLinkStore: INewPaymentLinkModalStore,
        @inject(PayByLinkPaginationStoreSymbol) paginationStore: PaginationContainerStoreType,
        @inject(PayByLinkPaymentLinkDetailsStoreSymbol)
        paymentLinkDetailsModalStore: TPaymentLinkDetailsModalStore,
        @inject(ProfileStoreSymbol) profileStore: ProfileStoreInterface,
        @inject(AvailableStoresDataStoreSymbol) merchantStoresStore: AvailableStoresDataStore,
        @inject(PayByLinkFilterButtonStoreSymbol) filterStore: IFilterContainerStore
    ) {
        this._rangePickerStore = rangePickerStore
        this._overviewModalStore = overviewModalStore
        this._newPaymentLinkModalStore = newPaymentLinkModalStore
        this._createNewPaymentLinkStore = createNewPaymentLinkStore
        this._paginationStore = paginationStore
        this._paymentLinkDetailsModalStore = paymentLinkDetailsModalStore
        this._profileStore = profileStore
        this._merchantStoresStore = merchantStoresStore
        this.filterStore = filterStore

        makeObservable(this, {
            isLoading: observable,
            _paymentLinks: observable,
            selectedPaymentLink: observable,
            createdPaymentLink: observable,
            mobileTableViewType: observable,
            status: observable,
            isNewPaymentLinkNotificationModalOpen: observable,

            columns: computed,
            paymentLinks: computed,
            request: computed,
            merchantId: computed,
            preferences: computed,
            ecomStoresExist: computed,

            loadPaymentLinks: action.bound,
            openNewPaymentLinkModal: action.bound,
            openNewPaymentLinkNotificationModal: action.bound,
            setStatus: action.bound,
            openOverviewModal: action.bound,
            openPaymentDetailsModal: action.bound
        })

        reaction(
            () => ({
                isNewLinkCreated: this._createNewPaymentLinkStore.loadingState === LoadingState.DONE
            }),
            ({isNewLinkCreated}) => {
                if (isNewLinkCreated) {
                    this.loadPaymentLinks()
                }
            }
        )

        when(
            () => Boolean(this.merchantId),
            () => this.initializePreferences()
        )

        /** reaction:preferences */
        reaction(
            () => this.preferences,
            () => {
                if (!this._isInitialized) {
                    return
                }
                if (this._paginationStore.paging.page === 1) {
                    this.loadPaymentLinks()
                } else {
                    this._paginationStore.setPageNumber(1)
                }
            }
        )

        reaction(
            () => [this._paginationStore.pageNumber, this.filterStore.submittedFilter],
            () => this.loadPaymentLinks()
        )
    }

    get ecomStoresExist(): boolean {
        return !!this._merchantStoresStore.ecomStores.length
    }

    setStatus(status: PayByLinkStatus) {
        this.status = status
    }

    setMobileTableViewType = (tableViewType: TableViewType) =>
        (this.mobileTableViewType = tableViewType)

    private initializePreferences = () => {
        const profileEmail = storage.get('email')
        const storageKey = `${TABLE_STORAGE_KEYS.paymentLinks}-${profileEmail}-${this.merchantId}`
        const value = storage.get(storageKey) && JSON.parse(storage.get(storageKey))

        if (value) {
            const {size, status, period: p, from, to} = value
            const period = p as PeriodType

            if (size) {
                this._paginationStore.setPageSize(size)
            }

            if (status) {
                this.setStatus(status)
            }

            if (period && period !== PeriodType.Custom) {
                this._rangePickerStore.setPeriod(period)
            } else if (from && to) {
                this._rangePickerStore.setDates([moment(from), moment(to)])
            }
        }

        /**
         * The 'reaction:preferences' in the constructor should not be executed.
         * Therefore the 'isInitialized' flag is set to true within a setTimeout.
         */
        setTimeout(() => (this._isInitialized = true))

        reaction(
            () => this.preferences,
            () => {
                if (profileEmail) {
                    const period = this._rangePickerStore.period
                    storage.set(storageKey, JSON.stringify({...this.preferences, period}))
                }
            }
        )
    }

    public openOverviewModal(selectedPaymentLink: PaymentLink) {
        this.selectedPaymentLink = selectedPaymentLink
        this._overviewModalStore.setOpen(true)
    }

    public openNewPaymentLinkModal() {
        if (!this.ecomStoresExist) {
            this.openNewPaymentLinkNotificationModal()
            return
        }

        this._newPaymentLinkModalStore.setOpen(true)
    }

    public openNewPaymentLinkNotificationModal() {
        this.isNewPaymentLinkNotificationModalOpen = true
    }

    public closeNewPaymentLinkNotificationModal() {
        this.isNewPaymentLinkNotificationModalOpen = false
    }

    public openPaymentDetailsModal(link) {
        this._paymentLinkDetailsModalStore.widgetType = 'linkDetails'
        this._paymentLinkDetailsModalStore.isNewLinkDetails = false
        this._paymentLinkDetailsModalStore.setPaymentLink(link)
    }

    public viewAllPayments(paymentLink: PaymentLink) {
        this._createNewPaymentLinkStore.viewAllPayments(paymentLink)
    }

    async loadPaymentLinks() {
        if (this.isLoading) {
            return
        }

        runInAction(() => (this.isLoading = true))

        const showError = (message: string) => {
            runInAction(() => {
                this._paymentLinks = []
                this._paginationStore.setTotal(0)
            })

            if (message) {
                openErrorNotification(message)
            }
        }

        try {
            const {result, error} = await getPaymentLinksV2(this.request)

            if (!error && result) {
                runInAction(() => (this._paymentLinks = result.records))
                this._paginationStore.setTotal(result.totalCount)
            } else {
                showError(error?.message)
            }
        } catch (error) {
            showError(i18next.t('globals:errors.general'))
        }

        runInAction(() => (this.isLoading = false))
    }
}
